import { useState } from 'react';
import { css } from '@emotion/react';
import { useHistory } from 'react-router-dom';

const pageStyles = css`
  min-height: 100vh;
  background: #fff;
  font-family: 'FlodeskSans', sans-serif;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

/* ── Top Nav ── */

const topNavStyles = css`
  width: 100%;
  height: 96px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 40px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  flex-shrink: 0;
`;

const logoStyles = css`
  width: 63px;
  height: 13px;
`;

const logoContainerStyles = css`
  width: 280px;
`;

const navTabsStyles = css`
  display: flex;
  gap: 16px;
  align-items: center;
`;

const navTabActiveStyles = css`
  font-size: 14px;
  font-weight: 500;
  color: #241a17;
  line-height: 20px;
`;

const navTabInactiveStyles = css`
  font-size: 14px;
  font-weight: 500;
  color: rgba(61, 46, 41, 0.52);
  line-height: 20px;
  cursor: pointer;

  &:hover {
    color: rgba(61, 46, 41, 0.72);
  }
`;

const closeContainerStyles = css`
  width: 280px;
  display: flex;
  justify-content: flex-end;
`;

const closeButtonStyles = css`
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;

  img {
    width: 12px;
    height: 12px;
  }

  &:hover {
    opacity: 0.7;
  }
`;

/* ── Content ── */

const contentStyles = css`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 64px;
  padding: 64px 0;
`;

const titleStyles = css`
  font-size: 30px;
  font-weight: 500;
  color: #000;
  text-align: center;
  letter-spacing: -0.3px;
  line-height: 36px;
  margin: 0;
`;

/* ── Summary Cards ── */

const summaryRowStyles = css`
  display: flex;
  gap: 36px;
`;

const summaryCardStyles = css`
  width: 240px;
  height: 174px;
  background: #fff;
  border-radius: 10px;
  box-shadow: 0 0 25px rgba(0, 0, 0, 0.11);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 9px;
`;

const summaryValueStyles = css`
  font-size: 30px;
  font-weight: 400;
  color: #000;
  text-align: center;
  letter-spacing: 0.3px;
  line-height: 30px;
  margin: 0;
`;

const summaryLabelStyles = css`
  font-size: 13px;
  font-weight: 500;
  color: #898989;
  text-align: center;
  letter-spacing: 0.13px;
  line-height: 13px;
  margin: 0;
`;

/* ── Subject Cards ── */

const cardsRowStyles = css`
  display: flex;
  gap: 24px;
`;

const subjectCardStyles = css`
  width: 384px;
  height: 505px;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 0 25px rgba(0, 0, 0, 0.11);
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 40px 52px;
  position: relative;
`;

const expandButtonStyles = css`
  position: absolute;
  bottom: 30px;
  right: 30px;
  width: 24px;
  height: 24px;
  cursor: pointer;
  background: none;
  border: none;
  padding: 0;

  img {
    width: 24px;
    height: 24px;
  }

  &:hover {
    opacity: 0.7;
  }
`;

const subjectCardTitleStyles = css`
  font-size: 24px;
  font-weight: 500;
  color: #000;
  text-align: center;
  letter-spacing: -0.24px;
  line-height: 30px;
  margin: 0;
`;

const subjectCardSubtitleStyles = css`
  font-size: 14px;
  font-weight: 400;
  color: #241a17;
  text-align: center;
  line-height: 20px;
  margin: 12px 0 0;
`;

/* ── Donut Chart ── */

const donutContainerStyles = css`
  width: 187px;
  height: 187px;
  margin-top: 48px;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const donutTextStyles = css`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
`;

const donutValueStyles = css`
  font-size: 30px;
  font-weight: 400;
  color: #000;
  letter-spacing: 0.3px;
  line-height: 30px;
  margin: 0;
`;

const donutLabelStyles = css`
  font-size: 13px;
  font-weight: 500;
  color: #898989;
  letter-spacing: 0.13px;
  line-height: 13px;
  margin: 0;
`;

/* ── Stats Row ── */

const statsRowStyles = css`
  display: flex;
  justify-content: space-between;
  width: 280px;
  margin-top: auto;
  margin-bottom: 8px;
`;

const statStyles = css`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  width: 76px;
`;

const statValueStyles = css`
  font-size: 24px;
  font-weight: 400;
  color: #000;
  letter-spacing: 0.24px;
  line-height: 30px;
  margin: 0;
  text-align: center;
`;

const statLabelStyles = css`
  font-size: 13px;
  font-weight: 500;
  color: #898989;
  letter-spacing: 0.13px;
  line-height: 18px;
  margin: 0;
  text-align: center;
`;

type DonutProps = {
  percentage: number;
  color: string;
  bgColor: string;
};

function Donut({ percentage, color, bgColor }: DonutProps) {
  const size = 187;
  const strokeWidth = 14;
  const radius = (size - strokeWidth) / 2;
  const center = size / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (percentage / 100) * circumference;

  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      <circle
        cx={center}
        cy={center}
        r={radius}
        fill="none"
        stroke={bgColor}
        strokeWidth={strokeWidth}
      />
      <circle
        cx={center}
        cy={center}
        r={radius}
        fill="none"
        stroke={color}
        strokeWidth={strokeWidth}
        strokeDasharray={circumference}
        strokeDashoffset={offset}
        transform={`rotate(-90 ${center} ${center})`}
      />
    </svg>
  );
}

export default function AbResults() {
  const history = useHistory();
  const [tab, setTab] = useState<'ab' | 'analytics'>('ab');

  return (
    <div css={pageStyles}>
      <nav css={topNavStyles}>
        <div css={logoContainerStyles}>
          <img css={logoStyles} src="/flodesk-logo.svg" alt="Flodesk" />
        </div>
        <div css={navTabsStyles}>
          <span
            css={tab === 'ab' ? navTabActiveStyles : navTabInactiveStyles}
            onClick={() => setTab('ab')}
            role="button"
            tabIndex={0}
            onKeyDown={e => {
              if (e.key === 'Enter') setTab('ab');
            }}
          >
            A/B Test
          </span>
          <span
            css={tab === 'analytics' ? navTabActiveStyles : navTabInactiveStyles}
            onClick={() => setTab('analytics')}
            role="button"
            tabIndex={0}
            onKeyDown={e => {
              if (e.key === 'Enter') setTab('analytics');
            }}
          >
            Analytics
          </span>
        </div>
        <div css={closeContainerStyles}>
          <button type="button" css={closeButtonStyles} onClick={() => history.push('/dashboard')}>
            <img src="/close-icon.svg" alt="Close" />
          </button>
        </div>
      </nav>

      {tab === 'ab' && (
        <div css={contentStyles}>
          <h1 css={titleStyles}>Check how the A/B test is going</h1>

          <div css={summaryRowStyles}>
            <div css={summaryCardStyles}>
              <p css={summaryValueStyles}>2h 15m</p>
              <p css={summaryLabelStyles}>Test time left</p>
            </div>
            <div css={summaryCardStyles}>
              <p css={summaryValueStyles}>20%</p>
              <p css={summaryLabelStyles}>Sample %</p>
            </div>
            <div css={summaryCardStyles}>
              <p css={summaryValueStyles}>160</p>
              <p css={summaryLabelStyles}>Sample size</p>
            </div>
          </div>

          <div css={cardsRowStyles}>
            {/* Subject Line A */}
            <div css={subjectCardStyles}>
              <h3 css={subjectCardTitleStyles}>Subject line A</h3>
              <p css={subjectCardSubtitleStyles}>Flash sales are here ⚡️</p>
              <div css={donutContainerStyles}>
                <div
                  css={css`
                    position: absolute;
                    inset: 0;
                  `}
                >
                  <Donut percentage={63.3} color="#6BA3F7" bgColor="#E8EFF9" />
                </div>
                <div css={donutTextStyles}>
                  <p css={donutValueStyles}>63.3%</p>
                  <p css={donutLabelStyles}>Open rate</p>
                </div>
              </div>
              <div css={statsRowStyles}>
                <div css={statStyles}>
                  <p css={statValueStyles}>80</p>
                  <p css={statLabelStyles}>Recipients</p>
                </div>
                <div css={statStyles}>
                  <p css={statValueStyles}>25</p>
                  <p css={statLabelStyles}>Opens</p>
                </div>
                <div css={statStyles}>
                  <p css={statValueStyles}>5</p>
                  <p css={statLabelStyles}>Clicks</p>
                </div>
              </div>
              <button type="button" css={expandButtonStyles}>
                <img src="/expand-icon.svg" alt="Expand" />
              </button>
            </div>

            {/* Subject Line B */}
            <div css={subjectCardStyles}>
              <h3 css={subjectCardTitleStyles}>Subject line B</h3>
              <p css={subjectCardSubtitleStyles}>Up to 50% off with our flash sales</p>
              <div css={donutContainerStyles}>
                <div
                  css={css`
                    position: absolute;
                    inset: 0;
                  `}
                >
                  <Donut percentage={32} color="#C4B5F5" bgColor="#EEEAF5" />
                </div>
                <div css={donutTextStyles}>
                  <p css={donutValueStyles}>32%</p>
                  <p css={donutLabelStyles}>Open rate</p>
                </div>
              </div>
              <div css={statsRowStyles}>
                <div css={statStyles}>
                  <p css={statValueStyles}>80</p>
                  <p css={statLabelStyles}>Recipients</p>
                </div>
                <div css={statStyles}>
                  <p css={statValueStyles}>12</p>
                  <p css={statLabelStyles}>Opens</p>
                </div>
                <div css={statStyles}>
                  <p css={statValueStyles}>1</p>
                  <p css={statLabelStyles}>Clicks</p>
                </div>
              </div>
              <button type="button" css={expandButtonStyles}>
                <img src="/expand-icon.svg" alt="Expand" />
              </button>
            </div>
          </div>
        </div>
      )}

      {tab === 'analytics' && (
        <div css={contentStyles}>
          <h1 css={titleStyles}>Explore the story of your send</h1>

          {/* At a glance */}
          <div
            css={css`
              width: 792px;
            `}
          >
            <div
              css={css`
                width: 492px;
                margin: 0 auto;
              `}
            >
              <h2
                css={css`
                  font-size: 24px;
                  font-weight: 500;
                  color: #000;
                  letter-spacing: -0.24px;
                  margin: 0 0 4px;
                `}
              >
                At a glance
              </h2>
              <p
                css={css`
                  font-size: 15px;
                  color: #424242;
                  line-height: 24px;
                  margin: 0;
                  letter-spacing: 0.15px;
                `}
              >
                <span
                  css={css`
                    font-weight: 500;
                  `}
                >
                  New recipes for the new year
                </span>
                <br />
                was sent out to 2,352 subscribers
                <br />
                on June 4, 2020 at 5:32am.
              </p>
            </div>
            <div
              css={css`
                display: flex;
                gap: 36px;
                align-items: flex-end;
                height: 383px;
                margin: 102px auto 12px;
                width: 492px;
              `}
            >
              <div
                css={css`
                  display: flex;
                  flex-direction: column;
                  align-items: center;
                  gap: 12px;
                `}
              >
                <div
                  css={css`
                    width: 96px;
                    height: 383px;
                    background: #b7be7b;
                    border-radius: 2px;
                  `}
                />
                <div
                  css={css`
                    text-align: center;
                    font-size: 15px;
                    font-weight: 500;
                    color: #424242;
                  `}
                >
                  <p
                    css={css`
                      margin: 0;
                      line-height: 21px;
                    `}
                  >
                    2,235
                  </p>
                  <p
                    css={css`
                      margin: 0;
                      line-height: 21px;
                    `}
                  >
                    Visits
                  </p>
                </div>
              </div>
              <div
                css={css`
                  display: flex;
                  flex-direction: column;
                  align-items: center;
                  gap: 12px;
                `}
              >
                <div
                  css={css`
                    width: 96px;
                    height: 371px;
                    background: #f4df58;
                    border-radius: 2px;
                  `}
                />
                <div
                  css={css`
                    text-align: center;
                    font-size: 15px;
                    font-weight: 500;
                    color: #424242;
                  `}
                >
                  <p
                    css={css`
                      margin: 0;
                      line-height: 21px;
                    `}
                  >
                    2,109
                  </p>
                  <p
                    css={css`
                      margin: 0;
                      line-height: 21px;
                    `}
                  >
                    Product click
                  </p>
                </div>
              </div>
              <div
                css={css`
                  display: flex;
                  flex-direction: column;
                  align-items: center;
                  gap: 12px;
                `}
              >
                <div
                  css={css`
                    width: 96px;
                    height: 137px;
                    background: #607cd2;
                    border-radius: 2px;
                  `}
                />
                <div
                  css={css`
                    text-align: center;
                    font-size: 15px;
                    font-weight: 500;
                    color: #424242;
                  `}
                >
                  <p
                    css={css`
                      margin: 0;
                      line-height: 21px;
                    `}
                  >
                    895
                  </p>
                  <p
                    css={css`
                      margin: 0;
                      line-height: 21px;
                    `}
                  >
                    Opens
                  </p>
                </div>
              </div>
              <div
                css={css`
                  display: flex;
                  flex-direction: column;
                  align-items: center;
                  gap: 12px;
                `}
              >
                <div
                  css={css`
                    width: 96px;
                    height: 27px;
                    background: #e2a32f;
                    border-radius: 2px;
                  `}
                />
                <div
                  css={css`
                    text-align: center;
                    font-size: 15px;
                    font-weight: 500;
                    color: #424242;
                    letter-spacing: 0.15px;
                  `}
                >
                  <p
                    css={css`
                      margin: 0;
                      line-height: 21px;
                    `}
                  >
                    418
                  </p>
                  <p
                    css={css`
                      margin: 0;
                      line-height: 21px;
                    `}
                  >
                    Clicks
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Engagement */}
          <div
            css={css`
              width: 792px;
            `}
          >
            <h2
              css={css`
                font-size: 24px;
                font-weight: 500;
                color: #000;
                letter-spacing: -0.24px;
                margin: 0 0 40px;
              `}
            >
              Engagement
            </h2>
            <div
              css={css`
                display: flex;
                gap: 24px;
              `}
            >
              {/* Open rate card */}
              <div
                css={css`
                  width: 384px;
                  height: 505px;
                  background: #fff;
                  border-radius: 12px;
                  box-shadow: 0 0 25px rgba(0, 0, 0, 0.11);
                  display: flex;
                  flex-direction: column;
                  align-items: center;
                  padding: 40px 52px;
                  position: relative;
                `}
              >
                <h3
                  css={css`
                    font-size: 24px;
                    font-weight: 500;
                    color: #000;
                    letter-spacing: -0.24px;
                    margin: 0;
                  `}
                >
                  Open rate
                </h3>
                <div
                  css={css`
                    width: 187px;
                    height: 187px;
                    margin-top: 32px;
                    position: relative;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                  `}
                >
                  <div
                    css={css`
                      position: absolute;
                      inset: 0;
                    `}
                  >
                    <Donut percentage={63.3} color="#4A7DD7" bgColor="#D8E2F0" />
                  </div>
                  <div
                    css={css`
                      display: flex;
                      flex-direction: column;
                      align-items: center;
                      gap: 4px;
                      z-index: 1;
                    `}
                  >
                    <p
                      css={css`
                        font-size: 30px;
                        color: #000;
                        letter-spacing: 0.3px;
                        margin: 0;
                      `}
                    >
                      63.3%
                    </p>
                    <p
                      css={css`
                        font-size: 11px;
                        font-weight: 500;
                        color: #a5a5a5;
                        margin: 0;
                      `}
                    >
                      +3.2% on resend
                    </p>
                  </div>
                </div>
                <div
                  css={css`
                    display: flex;
                    justify-content: space-between;
                    width: 180px;
                    margin-top: auto;
                  `}
                >
                  <div
                    css={css`
                      text-align: center;
                    `}
                  >
                    <p
                      css={css`
                        font-size: 24px;
                        color: #000;
                        letter-spacing: 0.24px;
                        margin: 0;
                        line-height: 30px;
                      `}
                    >
                      895
                    </p>
                    <p
                      css={css`
                        font-size: 13px;
                        font-weight: 500;
                        color: #898989;
                        margin: 0;
                        line-height: 18px;
                      `}
                    >
                      Total
                    </p>
                  </div>
                  <div
                    css={css`
                      text-align: center;
                    `}
                  >
                    <p
                      css={css`
                        font-size: 24px;
                        color: #000;
                        letter-spacing: 0.24px;
                        margin: 0;
                        line-height: 30px;
                      `}
                    >
                      813
                    </p>
                    <p
                      css={css`
                        font-size: 13px;
                        font-weight: 500;
                        color: #898989;
                        margin: 0;
                        line-height: 18px;
                      `}
                    >
                      Unique
                    </p>
                  </div>
                </div>
                <button type="button" css={expandButtonStyles}>
                  <img src="/expand-icon.svg" alt="Expand" />
                </button>
              </div>
              {/* Click rate card */}
              <div
                css={css`
                  width: 384px;
                  height: 505px;
                  background: #fff;
                  border-radius: 12px;
                  box-shadow: 0 0 25px rgba(0, 0, 0, 0.11);
                  display: flex;
                  flex-direction: column;
                  align-items: center;
                  padding: 40px 52px;
                  position: relative;
                `}
              >
                <h3
                  css={css`
                    font-size: 24px;
                    font-weight: 500;
                    color: #000;
                    letter-spacing: -0.24px;
                    margin: 0;
                  `}
                >
                  Click rate
                </h3>
                <div
                  css={css`
                    width: 187px;
                    height: 187px;
                    margin-top: 32px;
                    position: relative;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                  `}
                >
                  <div
                    css={css`
                      position: absolute;
                      inset: 0;
                    `}
                  >
                    <Donut percentage={23.8} color="#D4A23A" bgColor="#EDE4D0" />
                  </div>
                  <div
                    css={css`
                      display: flex;
                      flex-direction: column;
                      align-items: center;
                      gap: 4px;
                      z-index: 1;
                    `}
                  >
                    <p
                      css={css`
                        font-size: 30px;
                        color: #000;
                        letter-spacing: 0.3px;
                        margin: 0;
                      `}
                    >
                      23.8%
                    </p>
                    <p
                      css={css`
                        font-size: 11px;
                        font-weight: 500;
                        color: #a5a5a5;
                        margin: 0;
                      `}
                    >
                      +4.1% on resend
                    </p>
                  </div>
                </div>
                <div
                  css={css`
                    display: flex;
                    justify-content: space-between;
                    width: 180px;
                    margin-top: auto;
                  `}
                >
                  <div
                    css={css`
                      text-align: center;
                    `}
                  >
                    <p
                      css={css`
                        font-size: 24px;
                        color: #000;
                        letter-spacing: 0.24px;
                        margin: 0;
                        line-height: 30px;
                      `}
                    >
                      418
                    </p>
                    <p
                      css={css`
                        font-size: 13px;
                        font-weight: 500;
                        color: #898989;
                        margin: 0;
                        line-height: 18px;
                      `}
                    >
                      Total
                    </p>
                  </div>
                  <div
                    css={css`
                      text-align: center;
                    `}
                  >
                    <p
                      css={css`
                        font-size: 24px;
                        color: #000;
                        letter-spacing: 0.24px;
                        margin: 0;
                        line-height: 30px;
                      `}
                    >
                      403
                    </p>
                    <p
                      css={css`
                        font-size: 13px;
                        font-weight: 500;
                        color: #898989;
                        margin: 0;
                        line-height: 18px;
                      `}
                    >
                      Unique
                    </p>
                  </div>
                </div>
                <button type="button" css={expandButtonStyles}>
                  <img src="/expand-icon.svg" alt="Expand" />
                </button>
              </div>
            </div>
          </div>

          {/* Clicks by device */}
          <div
            css={css`
              width: 792px;
            `}
          >
            <h2
              css={css`
                font-size: 24px;
                font-weight: 500;
                color: #000;
                letter-spacing: -0.24px;
                margin: 0 0 40px;
              `}
            >
              Clicks by device
            </h2>
            <div
              css={css`
                width: 792px;
                height: 633px;
                background: #fff;
                border-radius: 12px;
                box-shadow: 0 0 25px rgba(0, 0, 0, 0.11);
                display: flex;
                flex-direction: column;
                align-items: center;
                padding: 48px 40px;
              `}
            >
              <p
                css={css`
                  font-size: 24px;
                  color: #000;
                  letter-spacing: -0.24px;
                  margin: 0 0 48px;
                  text-align: center;
                `}
              >
                This email was opened more on desktop than mobile
              </p>
              <img
                css={css`
                  width: 363px;
                  height: 363px;
                `}
                src="/analytics-pie.svg"
                alt="Clicks by device pie chart"
              />
              <div
                css={css`
                  display: flex;
                  justify-content: space-between;
                  width: 100%;
                  margin-top: auto;
                `}
              >
                <div
                  css={css`
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    gap: 8px;
                  `}
                >
                  <img
                    css={css`
                      width: 32px;
                      height: 32px;
                    `}
                    src="/analytics-mobile-icon.svg"
                    alt=""
                  />
                  <p
                    css={css`
                      font-size: 15px;
                      font-weight: 500;
                      color: #424242;
                      text-align: center;
                      margin: 0;
                      line-height: 21.5px;
                      letter-spacing: 0.15px;
                    `}
                  >
                    36% opens
                    <br />
                    on mobile
                  </p>
                </div>
                <div
                  css={css`
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    gap: 8px;
                  `}
                >
                  <img
                    css={css`
                      width: 32px;
                      height: 32px;
                    `}
                    src="/analytics-desktop-icon.svg"
                    alt=""
                  />
                  <p
                    css={css`
                      font-size: 15px;
                      font-weight: 500;
                      color: #424242;
                      text-align: center;
                      margin: 0;
                      line-height: 21.5px;
                      letter-spacing: 0.15px;
                    `}
                  >
                    64% opens
                    <br />
                    on desktop
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Deliverability */}
          <div
            css={css`
              width: 792px;
            `}
          >
            <h2
              css={css`
                font-size: 24px;
                font-weight: 500;
                color: #000;
                letter-spacing: -0.24px;
                margin: 0 0 40px;
              `}
            >
              Deliverability
            </h2>
            <div
              css={css`
                width: 792px;
                height: 424px;
                background: #fff;
                border-radius: 12px;
                box-shadow: 0 0 25px rgba(0, 0, 0, 0.11);
                display: flex;
                padding: 48px 40px;
                gap: 40px;
              `}
            >
              <div
                css={css`
                  width: 300px;
                  position: relative;
                `}
              >
                <img
                  css={css`
                    width: 244px;
                    height: 162px;
                    position: absolute;
                    top: 80px;
                    left: 0;
                  `}
                  src="/analytics-deliver-ring.svg"
                  alt=""
                />
                <img
                  css={css`
                    width: 90px;
                    height: 85px;
                    position: absolute;
                    top: 157px;
                    left: 116px;
                  `}
                  src="/analytics-deliver-accent.svg"
                  alt=""
                />
              </div>
              <div
                css={css`
                  flex: 1;
                `}
              >
                <p
                  css={css`
                    font-size: 24px;
                    color: #000;
                    letter-spacing: -0.24px;
                    margin: 0 0 80px;
                    text-align: center;
                  `}
                >
                  99.7% of your emails were delivered
                </p>
                <p
                  css={css`
                    font-size: 15px;
                    font-weight: 500;
                    color: #000;
                    letter-spacing: -0.15px;
                    margin: 0 0 8px;
                  `}
                >
                  Off the chart
                </p>
                <p
                  css={css`
                    font-size: 15px;
                    color: #424242;
                    line-height: 24px;
                    letter-spacing: 0.15px;
                    margin: 0;
                  `}
                >
                  2,345 out of 2,356 emails were delivered without bouncing.
                </p>
              </div>
            </div>
          </div>

          {/* Bounces, Unsubscribes, Spam */}
          <div
            css={css`
              width: 792px;
            `}
          >
            <h2
              css={css`
                font-size: 24px;
                font-weight: 500;
                color: #000;
                letter-spacing: -0.24px;
                margin: 0 0 40px;
              `}
            >
              Bounces, Unsubscribes, Spam
            </h2>
            <div
              css={css`
                display: flex;
                gap: 36px;
                margin-bottom: 32px;
              `}
            >
              <div css={summaryCardStyles}>
                <p css={summaryValueStyles}>24</p>
                <p css={summaryLabelStyles}>Bounces</p>
              </div>
              <div css={summaryCardStyles}>
                <p css={summaryValueStyles}>5</p>
                <p css={summaryLabelStyles}>Unsubscribed</p>
              </div>
              <div css={summaryCardStyles}>
                <p css={summaryValueStyles}>1</p>
                <p css={summaryLabelStyles}>Marked spam</p>
              </div>
            </div>
            <div
              css={css`
                display: flex;
                gap: 36px;
              `}
            >
              <p
                css={css`
                  width: 240px;
                  font-size: 13px;
                  color: #424242;
                  line-height: 20px;
                  letter-spacing: 0.26px;
                  margin: 0;
                `}
              >
                No subscribers were removed from your list. Every email was delivered.
              </p>
              <p
                css={css`
                  width: 240px;
                  font-size: 13px;
                  color: #424242;
                  line-height: 20px;
                  letter-spacing: 0.26px;
                  margin: 0;
                `}
              >
                They removed themselves from your list.
              </p>
              <p
                css={css`
                  width: 240px;
                  font-size: 13px;
                  color: #424242;
                  line-height: 20px;
                  letter-spacing: 0.26px;
                  margin: 0;
                `}
              >
                They marked your email as spam.{' '}
                <button
                  type="button"
                  css={css`
                    color: #898989;
                    text-decoration: underline;
                    background: none;
                    border: none;
                    padding: 0;
                    font: inherit;
                    cursor: pointer;
                  `}
                  onClick={() => {}}
                >
                  Learn more
                </button>
                .
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
